import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { numericFilterColumn } from '../../../utils/numericFilterColumn';
import PersonCell from '../../../components/ui/PersonCell';
import { positionAsync } from '../../positions/config';

export const PB_STATUSES = ['active', 'completed', 'cancelled', 'extended'];
export const PB_SOURCES = ['ats_integrated', 'standalone', 'external_webhook'];
export const PB_RISK_LEVELS = ['low', 'medium', 'high'];

const multiSelectFilter = (label, options, valueRender) => ({
  filter: true,
  sort: true,
  filterType: 'custom',
  filterOptions: {
    defaultOp: 'in',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0]
        ? filterList[index][0].split(',').filter(Boolean)
        : [];
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
              filterList[index] = csv ? [csv] : [];
              onChange(filterList[index], index, column);
            }}
            renderValue={(selected) => selected.map((s) => valueRender ? valueRender(s) : s).join(', ')}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>{valueRender ? valueRender(o) : o}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  },
});

const usePbPreboardingsApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/preboardings?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const create = useCallback(async (formData) => {
    try {
      const payload = {
        employeeId: formData.employeeId,
        managerEmployeeId: formData.managerEmployeeId,
        expectedFirstDay: formData.expectedFirstDay,
        buddyEmployeeId: formData.buddyEmployeeId || null,
        departmentId: formData.departmentId || null,
        positionId: formData.positionId || null,
        source: 'standalone',
        offerAcceptedAt: new Date().toISOString(),
      };
      await api.post('/v1/preboardings', payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const cancel = useCallback(async (id, reason) => {
    try {
      await api.post(`/v1/preboardings/${id}/cancel`, { reason });
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const close = useCallback(async (id, body) => {
    try {
      await api.post(`/v1/preboardings/${id}/close`, body || {});
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const columns = useMemo(() => {
    const statusLabel = (s) => t(`preboarding.status.${s}`, { defaultValue: s });
    const sourceLabel = (s) => t(`preboarding.source.${s}`, { defaultValue: s });
    const riskLabel = (r) => t(`preboarding.riskLevel.${r}`, { defaultValue: r });

    return [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      {
        name: 'personFullName',
        label: t('preboarding.personFullName'),
        options: {
          customBodyRender: (v, tableMeta) => (
            <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.personProfilePicture} />
          ),
          filter: true,
          sort: true,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like', filterKey: 'personName[like]' },
        },
      },
      {
        name: 'personEmail',
        label: t('preboarding.personEmail'),
        options: {
          filter: true,
          sort: true,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like', filterKey: 'personEmail[like]' },
        },
      },
      {
        name: 'status',
        label: t('preboarding.statusLabel'),
        options: {
          ...multiSelectFilter(t('preboarding.statusLabel'), PB_STATUSES, statusLabel),
          customBodyRender: (v) => statusLabel(v),
        },
      },
      {
        name: 'source',
        label: t('preboarding.sourceLabel'),
        options: {
          ...multiSelectFilter(t('preboarding.sourceLabel'), PB_SOURCES, sourceLabel),
          customBodyRender: (v) => sourceLabel(v),
        },
      },
      {
        name: 'noShowRiskLevel',
        label: t('preboarding.noShowRiskLevelLabel'),
        options: {
          ...multiSelectFilter(t('preboarding.noShowRiskLevelLabel'), PB_RISK_LEVELS, riskLabel),
          customBodyRender: (v) => riskLabel(v || 'low'),
        },
      },
      dateFilterColumn('offerAcceptedAt', t('preboarding.offerAcceptedAt'), lang),
      dateFilterColumn('expectedFirstDay', t('preboarding.expectedFirstDay'), lang),
      numericFilterColumn('npsScore', t('preboarding.npsScore'), { decimals: 0 }),
      {
        name: 'portalActivatedAt',
        label: t('preboarding.portalActivated'),
        options: {
          filter: false,
          sort: true,
          customBodyRender: (v) => v ? '✓' : '-',
        },
      },
    ];
  }, [t, lang, data]);

  const employeeAsync = useMemo(() => ({
    onSearch: async (q) => {
      const params = q ? { 'firstName[like]': q, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
      const r = res.data?.data;
      const items = Array.isArray(r) ? r : (r?.data || []);
      return items.map((e) => ({ id: e.id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture }));
    },
    onGetById: async (id) => {
      const res = await api.get(`/v1/employees/${id}`);
      const e = res.data?.data || {};
      return { id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture };
    },
  }), []);

  // Tracker subject must be someone who hasn't started working yet —
  // preboarding covers the pre-start window. Manager/buddy stay unrestricted.
  const newHireAsync = useMemo(() => ({
    onSearch: async (q) => {
      const params = { 'employmentDate[gte]': new Date().toISOString().split('T')[0], pageSize: 20 };
      if (q) params['firstName[like]'] = q;
      const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
      const r = res.data?.data;
      const items = Array.isArray(r) ? r : (r?.data || []);
      return items.map((e) => ({ id: e.id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture }));
    },
    onGetById: employeeAsync.onGetById,
  }), [employeeAsync]);

  const objectSchema = useMemo(() => ([
    {
      name: 'employeeId', label: t('preboarding.form.employee'), type: 'asyncSelect', grid: 6, required: true,
      ...newHireAsync,
      deriveFields: async (employeeId) => {
        if (!employeeId) return null;
        const res = await api.get(`/v1/employees/${employeeId}/manager`);
        const m = res.data?.data;
        return m?.employeeId ? { managerEmployeeId: m.employeeId } : null;
      },
    },
    { name: 'managerEmployeeId', label: t('preboarding.form.manager'), type: 'asyncSelect', grid: 6, required: true, ...employeeAsync },
    { name: 'expectedFirstDay', label: t('preboarding.form.expectedFirstDay'), type: 'date', grid: 6, required: true },
    { name: 'buddyEmployeeId', label: t('preboarding.form.buddy'), type: 'asyncSelect', grid: 6, ...employeeAsync },
    {
      name: 'departmentId', label: t('preboarding.form.department'), type: 'asyncSelect', grid: 6,
      onSearch: async (q) => {
        const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
        const res = await api.get('/v1/departments', { params });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        return items.map((d) => ({ id: d.id, name: d.name }));
      },
      onGetById: async (id) => {
        const res = await api.get(`/v1/departments/${id}`);
        const d = res.data?.data || {};
        return { id, name: d.name || '' };
      },
    },
    {
      name: 'positionId', label: t('preboarding.form.position'), type: 'asyncSelect', grid: 6,
      // Pozisyonun GÖRÜNEN ADI YOKTUR: /v1/positions -> PositionResponse yalnız
      // Code/DepartmentName/TitleName taşır (`name` alanı hiç yok) ve filtre sözleşmesi
      // `name[like]` tanımaz. Etiket ile arama parametresini burada elle kurmak yerine
      // deponun tek pozisyon seçici yardımcısı kullanılır.
      ...positionAsync,
      searchPlaceholder: t('position.pickerSearchPlaceholder'),
    },
  ]), [t, employeeAsync, newHireAsync]);

  return { fetching, data, pagination, columns, objectSchema, refresh, create, cancel, close };
};

export default usePbPreboardingsApi;
